import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

/**
 * Authenticated file uploader.
 * Requires the caller to be an admin (cookie-based session).
 * Props:
 *   accept: file accept string (e.g. "image/*", ".pdf,.mp3")
 *   onUpload: ({ file_id, url, content_type }) => void
 *   label: shown above the input
 *   current: optional current value (URL or path) to preview
 */
export default function FileUploader({ accept = "image/*", onUpload, label = "Upload file", current = "" }) {
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpload?.(data);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = ""; // allow re-selecting same file
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid="file-uploader">
      <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-lapis/30 cursor-pointer hover:border-lapis hover:bg-cream/60 text-sm text-lapis font-medium transition">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        {busy ? "Uploading…" : label}
        <input type="file" accept={accept} onChange={handle} className="hidden" disabled={busy} data-testid="file-uploader-input" />
      </label>
      {current && (
        <span className="text-xs text-ink/50 truncate max-w-[200px]" data-testid="file-uploader-current">
          {current.length > 40 ? "…" + current.slice(-30) : current}
        </span>
      )}
    </div>
  );
}
